<template>
  <div
    id="login-to-cloud"
    class="login-to-cloud d-flex flex-column align-center"
    :class="{ suspended: isSuspended }"
    role="main">
    <div
      :id="loginPageStyle"
      class="login-block d-flex flex-column flex-sm-fill align-center justify-center"
      :class="{ 'is-visible': isVisible }">
      <div class="logo-block text-center">
        <h4
          v-if="isSpecialLogin"
          class="login-logo-text">
          {{ specialTextLogo }}
        </h4>
        <template v-else>
          <h4
            :title="accountTitle"
            class="login-banner-text">
            {{ accountTitle }}
          </h4>
          <div class="login-logo-wrap">
            <img
              :src="logo"
              :alt="string.altLogoText"
              class="quatrix-logo">
          </div>
        </template>
      </div>
      <component
        :is="mfaStep"
        ref="loginForm"
        class="flex-sm-fill"
        @vue:mounted="loginComponentMounted"
        @successLogin="successLogin" />
    </div>
    <div class="copyright">
      <span v-html="$sanitize(string.copyrightText)" />
      <span v-if="isPrivacyPolicy">
        <span>|</span>
        <a
          :href="privacyPolicyUrl"
          class="black-link"
          target="_blank">{{ string.privacyPolicyLabel }}</a>
      </span>
    </div>
    <footer
      v-if="isStandAlonePage"
      class="d-flex justify-space-around align-center">
      <img
        alt="iso"
        src="@/assets/images/iso.svg">
      <img
        alt="governament"
        src="@/assets/images/governament.svg">
      <img
        alt="logo_pci"
        src="@/assets/images/logo_pci.svg">
      <img
        alt="hippa"
        src="@/assets/images/hippa.svg">
      <img
        alt="gdpr"
        src="@/assets/images/gdpr.svg">
      <img
        alt="skyhight"
        src="@/assets/images/skyhight.svg">
      <img
        alt="mcafee"
        src="@/assets/images/mcafee-secure-trustmark.svg">
    </footer>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { mapState, mapGetters } from 'vuex';

import LoginEmail from '@/components/auth/LoginEmail.vue';
import LoginSso from '@/components/auth/LoginSso.vue';
import LoginPassword from '@/components/auth/LoginPassword.vue';
import LoginSetup2fa from '@/components/auth/LoginSetup2fa.vue';
import Login2fa from '@/components/auth/Login2fa.vue';
import LoginPin from '@/components/auth/LoginPin.vue';
import Captcha from '@/components/auth/Captcha.vue';
import ForgotPassword from '@/components/auth/ForgotPassword.vue';
import LoginUnsetPassword from '@/components/auth/LoginUnsetPassword.vue';

import {
  SETUP_2FA_CODE,
  EMAIL_CODE,
  LOGIN_SSO,
  TWOFA_CODE,
  PIN_ON_SHARE_CODE,
  PIN_CODE,
  CAPTCHA_CODE,
  PASSWORD_CODE,
  EMAIL_REGISTRATION_CODE,
  FORGOT_PASSWORD_CODE,
} from '@/utils/cons';

export default {
  name: 'LoginPage',
  components: {
    LoginEmail,
    LoginSso,
    LoginSetup2fa,
    Login2fa,
    LoginPin,
    Captcha,
    ForgotPassword,
    LoginUnsetPassword,
  },
  emits: ['successLogin'],
  data() {
    return {
      isVisible: false,
      isPrivacyPolicy: (config.VUE_APP_IS_PRIVACY_POLICY || process.env.VUE_APP_IS_PRIVACY_POLICY || 'Y') === 'Y',
      privacyPolicyUrl: config.VUE_APP_PRIVACY_POLICY_URL || 'https://docs.maytech.net/quatrix/privacy-policy',
    };
  },
  computed: {
    ...mapState({
      mfaCode: (state) => state.auth.mfaCode,
      accountTitle: (state) => state.accountData.title,
      isStandAlonePage: (state) => state.isStandAlonePage,
      loginPageStyle: (store) => store.loginPageStyle,
      metadataLoaded: (store) => store.metadataLoaded,
    }),
    ...mapGetters({
      logo: 'getLoginLogo',
      isSuspended: 'isSuspended',
      isSpecialLogin: 'isSpecialLogin',
      specialTextLogo: 'specialTextLogo',
    }),

    string() {
      return {
        altLogoText: this.$gettext('Account logo'),
        copyrightText: this.$gettextInterpolate(
          this.$gettext('Quatrix%{ trademark } by %{ startTag } Maytech %{ closeTag }'),
          {
            trademark: '<sup>&reg;</sup>',
            startTag: '<a href="https://www.maytech.net/" class="black-link" rel="noopener" target="_blank">',
            closeTag: '</a>',
          },
          true
        ),
        privacyPolicyLabel: this.$gettext('Privacy Policy'),
      };
    },

    mfaStep() {
      switch (this.mfaCode) {
        case FORGOT_PASSWORD_CODE:
          return ForgotPassword;
        case SETUP_2FA_CODE:
          return LoginSetup2fa;
        case EMAIL_CODE:
          return LoginEmail;
        case LOGIN_SSO:
          return LoginSso;
        case TWOFA_CODE:
          return Login2fa;
        case PIN_ON_SHARE_CODE:
        case PIN_CODE:
          return LoginPin;
        // case CAPTCHA_CODE:
        //   return Captcha;
        case PASSWORD_CODE:
          return LoginPassword;
        case EMAIL_REGISTRATION_CODE:
          return LoginUnsetPassword;
        default:
          return LoginEmail;
      }
    },
  },
  watch: {
    metadataLoaded: {
      immediate: true,
      async handler() {
        if (this.isSpecialLogin) {
          require('@/assets/scss/special-login.scss');
        }
        if (!this.isVisible) {
          await nextTick();
          setTimeout(() => {
            this.isVisible = true;
          }, 150);
        }
      },
    },
  },
  methods: {
    loginComponentMounted() {
      this.$refs.loginForm.$el.querySelector('input[type=text], input[type=password]')?.focus();
    },
    successLogin() {
      this.$emit('successLogin');
    },
  },
};
</script>

<style lang="scss" scoped>
// adjust page height to IE message
body.ie-no-support .login-to-cloud {
  height: calc(100vh - 50px);

  @media screen and (max-width: 1372px) {
    height: calc(100vh - 60px);
  }

  @media screen and (max-width: 734px) {
    height: calc(100vh - 81px);
  }

  @media screen and (max-width: 516px) {
    height: calc(100vh - 102px);
  }
}

.login-to-cloud {
  width: 100%;
  height: 100vh;
  &.suspended {
    height: calc(100vh - #{$suspedned-account-height});
  }
  .login-block {
    margin: auto 0;
    opacity: 0;
    &.is-visible {
      opacity: 1;
    }
    .logo-block {
      max-width: 500px;
      padding-top: 30px;
      @media (max-width: 500px) {
        max-width: 400px;
      }
    }
    .login-banner-text {
      font-size: 18px;
      color: $base-grey;
      @extend %ellipsisOverflow;
    }
    .login-logo-wrap {
      margin: 20px auto 0;
      width: 300px;
      .quatrix-logo {
        height: 60px;
        max-width: 100%;
      }
    }
  }
}
.copyright {
  color: $base-grey;
  height: 30px;
  :deep sup {
    line-height: 25px;
  }
}
footer {
  height: 120px;
  padding: 15px 15%;
  background: $gray-100;
  width: 100vw;
  @media (max-width: 1366px) {
    padding: 15px 10%;
  }
  @media (max-width: 960px) {
    padding: 15px 6%;
  }
  @media (max-width: 766px) {
    padding: 15px 3%;
    height: 100px;
  }
  @media (max-width: 480px) {
    height: 60px;
  }
  img {
    transform: scale(1.2);
    @media (max-width: 766px) {
      transform: scale(1);
    }
    @media (max-width: 600px) {
      transform: scale(0.8);
    }
    @media (max-width: 480px) {
      transform: scale(0.6);
    }
    @media (max-width: 330px) {
      transform: scale(0.4);
    }
  }
}
</style>
